import React, { useEffect, useState } from 'react';
import { useParams, useHistory, Redirect } from 'react-router-dom';
import {
  Flex,
  Spinner,
  Text,
  Button,
  VStack,
  Icon,
  Box,
  Heading,
  ScaleFade,
} from '@chakra-ui/react';
import { isMobile } from '@/utils/isMobile';
import { MdOpenInNew, MdWeb } from 'react-icons/md';
import { colors } from '@/styles/theme';

const TaskOpenRedirector: React.FC = () => {
  const { taskId } = useParams<{ taskId: string }>();
  const history = useHistory();
  const [showFallback, setShowFallback] = useState(false);

  const openApp = () => {
    if (taskId) {
      window.location.href = `wecheckapp://task/${taskId}`;
    }
  };

  useEffect(() => {
    if (!isMobile()) return;

    openApp();

    const timer = setTimeout(() => {
      setShowFallback(true);
    }, 3000);

    const handleVisibilityChange = () => {
      if (document.hidden) {
        clearTimeout(timer);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [taskId]);

  if (!isMobile()) {
    return <Redirect to={`/app/tasks/activate/${taskId}`} />;
  }

  return (
    <Flex
      h="100vh"
      w="full"
      align="center"
      justify="center"
      direction="column"
      p={6}
      bg="gray.50"
    >
      <ScaleFade initialScale={0.9} in={true}>
        <Box
          maxW="400px"
          w="full"
          bg="white"
          p={8}
          borderRadius="2xl"
          boxShadow="2xl"
          textAlign="center"
        >
          <VStack spacing={6}>
            {!showFallback ? (
              <>
                <Spinner
                  size="xl"
                  color={colors.primary}
                  thickness="4px"
                  speed="0.65s"
                />
                <VStack spacing={2}>
                  <Heading size="md" color="gray.800">
                    Abrindo o WeCheck
                  </Heading>
                  <Text color="gray.600">
                    Estamos redirecionando você para o aplicativo...
                  </Text>
                </VStack>
              </>
            ) : (
              <ScaleFade initialScale={0.9} in={showFallback}>
                <VStack spacing={6}>
                  <Box
                    bg="green.50"
                    p={4}
                    borderRadius="full"
                    color={colors.primary}
                    fontSize="4xl"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Icon as={MdOpenInNew} />
                  </Box>
                  <VStack spacing={2}>
                    <Heading size="md" color="gray.800">
                      Não conseguiu abrir o app?
                    </Heading>
                    <Text color="gray.600">
                      Parece que o aplicativo não abriu automaticamente. Certifique-se de que ele está instalado.
                    </Text>
                  </VStack>

                  <VStack w="full" spacing={5}>
                    <Button
                      leftIcon={<Icon as={MdOpenInNew} />}
                      bg={colors.primary}
                      color="white"
                      w="full"
                      size="lg"
                      onClick={openApp}
                      borderRadius="xl"
                      _hover={{ bg: colors.primary, transform: 'translateY(-2px)' }}
                      _active={{ bg: colors.primary }}
                      transition="all 0.2s"
                    >
                      Tentar abrir no App
                    </Button>
                    <Button
                      leftIcon={<Icon as={MdWeb} />}
                      variant="outline"
                      colorScheme="gray"
                      w="full"
                      size="lg"
                      onClick={() => history.push(`/app/tasks/activate/${taskId}`)}
                      borderRadius="xl"
                      _hover={{ bg: 'gray.50', transform: 'translateY(-2px)' }}
                      transition="all 0.2s"
                    >
                      Continuar no Navegador
                    </Button>
                  </VStack>
                </VStack>
              </ScaleFade>
            )}
          </VStack>
        </Box>
      </ScaleFade>
    </Flex>
  );
};

export default TaskOpenRedirector;
